import { useEffect, useState, useMemo } from "react";
// import { Popup } from "semantic-ui-react";
import { Stack, TextField } from "@fluentui/react";
import { Send28Filled } from "@fluentui/react-icons";
import { useUser } from "../../contexts/useUserData";
import { useMediaQuery, useMediaQueries } from "@react-hook/media-query";
import { scopeValuesQuestionInput, hagstagValuesQuestionInput } from "../../infraestructure/models";

import styles from "./QuestionInput.module.css";

interface Props {
    onSend: (question: string) => void;
    disabled: boolean;
    placeholder?: string;
    clearOnSend?: boolean;
}

export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend }: Props) => {
    const [question, setQuestion] = useState<string>("");
    const { user, scope, token, seccion, priorityGroups } = useUser(); // Asegúrate de obtener estos valores desde el contexto correctamente
    const isUnder500px = useMediaQuery("only screen and (max-width: 500px)");
    const [isListVisible, setListVisible] = useState(false);

    const sendQuestion = async () => {
        // Validación previa
        if (disabled || !question.trim()) {
            console.error("Cannot send an empty or disabled question.");
            return;
        }
    
        if (!user || !scope) {
            console.error("User or scope is undefined. Cannot send question.");
            return;
        }
    
        onSend(question); // Llamada a la función de envío en el frontend
    
        // Limpiar la pregunta después de enviarla
        if (clearOnSend) {
            setQuestion("");
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setQuestion(newValue || "");
    };

    const updateQuestionWithHashTag = (hashTag: string) => {
        setQuestion(prevQuestion => prevQuestion + hashTag + " ");
    };

    const showList = () => setListVisible(true);
    const hideList = () => setListVisible(false);

    useEffect(() => {
        const popupElement = document.getElementById("popupHagstag");
        const listElement = document.getElementsByClassName(styles.list)?.item(0);

        if (isUnder500px) {
            popupElement?.classList.add(`${styles.popupResponsive}`);
            listElement?.classList.add(`${styles.listResponsive}`);
        } else {
            popupElement?.classList.remove(`${styles.popupResponsive}`);
            listElement?.classList.remove(`${styles.listResponsive}`);
        }
    }, [isUnder500px]);

    const filteredHashtagValues = useMemo(() => {
        if (
            !priorityGroups ||
            (!priorityGroups.priorityGroups_groupNames?.length && !priorityGroups.priorityGroups_valueNames?.length)
        ) {
            return hagstagValuesQuestionInput;
        }

        const groupNames: string[] = priorityGroups.priorityGroups_groupNames || [];
        const valueNames: string[] = priorityGroups.priorityGroups_valueNames || [];
        const groupLenghts: number[] = priorityGroups.priorityGroups_groupLenghts || [];

        // ...existing code...
        const mapping: Record<string, string[]> = {};

        // Caso base: no hay valores -> crear grupos vacíos (aunque luego se depuren)
        if (!valueNames.length) {
            groupNames.forEach(g => (mapping[g] = []));
        } else if (groupNames.length && groupLenghts.length === groupNames.length) {
            // Asignación estrictamente secuencial según las longitudes declaradas.
            let cursor = 0;
            groupNames.forEach((g, idx) => {
                const rawLen = groupLenghts[idx];
                const len = Number.isFinite(rawLen) ? Math.max(0, rawLen) : 0;
                mapping[g] = valueNames.slice(cursor, cursor + len);
                cursor += len;
            });
            // Sobrantes (overflow) se ignoran para cumplir estrictamente las longitudes.
            if (cursor < valueNames.length) {
                const overflow = valueNames.slice(cursor);
                console.warn('[QuestionInput] Hay hashtags sobrantes que no caben en los grupos definidos:', {
                    groupNames,
                    groupLenghts,
                    valueNamesTotal: valueNames.length,
                    capacidadDeclarada: groupLenghts.reduce((a,b)=>a+Math.max(0,b||0),0),
                    overflow
                });
                // Si quisieras meterlos en un grupo adicional, descomenta:
                // mapping['Otros'] = overflow;
            }
        } else {
            // Fallback mínimo: todo a un único grupo (primero si existe) o grupo genérico.
            const fallback = groupNames[0] || 'Sin categoría';
            mapping[fallback] = [...valueNames];
        }

        // Limpieza: eliminar grupos vacíos (si un grupo tuvo longitud 0 y no recibió sobrantes)
        Object.keys(mapping).forEach(g => { if (!mapping[g].length) delete mapping[g]; });

        return mapping;
    }, [priorityGroups, hagstagValuesQuestionInput]);

    const groupedColumns = useMemo(() => {
        const entries = Object.entries(filteredHashtagValues)
            .map(([name, hashtags], index) => ({ name, hashtags, size: hashtags.length, index }))
            .filter(e => e.size > 0);

        if (!entries.length) return { left: [], right: [] };
        if (entries.length === 1) return { left: [], right: [entries[0]] };

        let largest = entries[0];
        for (const e of entries) {
            if (e.size > largest.size) largest = e;
        }

        const others = entries.filter(e => e !== largest);
        const total = entries.reduce((s, g) => s + g.size, 0);
        const target = total / 2;

        const reachable: Record<number, number[]> = { 0: [] };
        others.forEach((g, idx) => {
            const currentSums = Object.keys(reachable).map(Number).sort((a,b)=>b-a);
            currentSums.forEach(sum => {
                const newSum = sum + g.size;
                if (reachable[newSum] === undefined) {
                    reachable[newSum] = [...reachable[sum], idx];
                }
            });
        });

    interface BestChoice { diff: number; sumLeft: number; indices: number[] }
    let best: BestChoice | null = null;
        Object.entries(reachable).forEach(([sumStr, idxList]) => {
            const sumLeftOthers = Number(sumStr);
            const sumRightOthers = (others.reduce((s,g)=>s+g.size,0)) - sumLeftOthers;
            const leftTotal = sumLeftOthers;
            const rightTotal = total - leftTotal;
            const diff = Math.abs(rightTotal - leftTotal);
            if (!best || diff < best.diff || (diff === best.diff && leftTotal < best.sumLeft)) {
                best = { diff, sumLeft: leftTotal, indices: idxList };
            }
        });

        let bestIndices: number[] = [];
        if (best) {
            bestIndices = (best as BestChoice).indices;
        }
        const leftIndicesSet = new Set(bestIndices);
        const left: typeof others = [];
        const right: typeof others = [largest];
        others.forEach((g, idx) => {
            if (leftIndicesSet.has(idx)) left.push(g); else right.push(g);
        });

        return { left, right };
    }, [filteredHashtagValues]);

    console.log("Filtered Hashtag Values:", filteredHashtagValues);

    return (
        <Stack
            horizontal
            className={
                disabled
                    ? `${styles.questionInputContainer} ${styles.questionIpuntContainerDisabled}`
                    : styles.questionInputContainer
            }
        >
            {seccion === "B2C" && (
                <>
                    <button className={styles.circlebtn} onClick={showList}>
                        #
                    </button>
                    <div className={`${styles.popUp}`} id="popupHagstag">
                        <strong>
                            <u>Selecciona un # para buscar solo en ese modo</u>
                        </strong>
                        <div className={styles.categoryContainer}>
                            <div className={styles.column}>
                                {groupedColumns.left.map(group => (
                                    <div key={group.name} className={styles.category}>
                                        <h4><u>{group.name}</u></h4>
                                        <ul className={styles.list}>
                                            {group.hashtags.map(h => (
                                                <li key={h} onClick={() => updateQuestionWithHashTag(h)}>{h}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                            <div className={styles.column}>
                                {groupedColumns.right.map(group => (
                                    <div key={group.name} className={`${styles.category} ${group.name === groupedColumns.right[0].name ? styles.modosEspeciales1004 : ''}`}>
                                        <h4><u>{group.name}</u></h4>
                                        <ul className={styles.list}>
                                            {group.hashtags.map(h => (
                                                <li key={h} onClick={() => updateQuestionWithHashTag(h)}>{h}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}
            <TextField
                className={styles.questionInputTextArea}
                placeholder={placeholder}
                multiline
                resizable={false}
                borderless
                value={question}
                onChange={onQuestionChange}
                onKeyDown={onEnterPress}
            />
            <div className={styles.questionInputButtonsContainer}>
                <div
                    className={`${styles.questionInputSendButton} ${
                        disabled || !question.trim() ? styles.questionInputSendButtonDisabled : ""
                    }`}
                    aria-label="Ask question button"
                    onClick={sendQuestion}
                >
                    <Send28Filled primaryFill="rgba(115, 118, 225, 1)" />
                </div>
            </div>
        </Stack>
    );
};
